/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import type { HotelRecentSearch, RoomConfig, Suggestions } from '../types';
import { MAX_CONTINUE_SEARCHES, CACHE_MAX_AGE } from '../configs/constants';
import { isQueryKeyEqual } from './queryKeyUtils';

const CONTINUE_SEARCHES_QUERY_KEY = [
  QUERY_PERSIST_CACHE,
  'hotels',
  'continueSearches',
] as const;

const EMPTY_CONTINUE_SEARCHES: HotelRecentSearch[] = [];

const isContinueSearchesQuery = (queryKey: readonly unknown[]) =>
  isQueryKeyEqual(queryKey, CONTINUE_SEARCHES_QUERY_KEY);

export function useContinueSearches() {
  const queryClient = useQueryClient();

  useMemo(() => {
    queryClient.setQueryDefaults(CONTINUE_SEARCHES_QUERY_KEY, {
      staleTime: CACHE_MAX_AGE,
      gcTime: CACHE_MAX_AGE,
    });
  }, [queryClient]);

  const { data: continueSearches = EMPTY_CONTINUE_SEARCHES } = useQuery<
    HotelRecentSearch[]
  >({
    queryKey: CONTINUE_SEARCHES_QUERY_KEY,
    // Cache-only read, matching flights' useRecentSearches: the fn exists so an
    // empty/busted cache cannot throw "No queryFn", and `enabled: false` stops
    // it running and overwriting restored/mutated entries with the default.
    queryFn: () => EMPTY_CONTINUE_SEARCHES,
    staleTime: CACHE_MAX_AGE,
    gcTime: CACHE_MAX_AGE,
    enabled: false,
  });

  const continueSearchesRef = useRef(continueSearches);
  continueSearchesRef.current = continueSearches;

  const addContinueSearch = useCallback(
    async (
      to: Suggestions,
      checkin: string = '',
      checkout: string = '',
      roomConfigs: RoomConfig[] = [{ id: '1', adults: 2, children: [] }],
    ) => {
      // Only save city searches, not hotel searches
      if (to.type !== 'city') {
        return;
      }

      // Get current searches from cache
      const existingSearches =
        queryClient.getQueryData<HotelRecentSearch[]>(
          CONTINUE_SEARCHES_QUERY_KEY,
        ) || [];

      const newSearch: HotelRecentSearch = {
        destination: {
          id: to.id,
          aa_id: to.aa_id,
          name: to.name,
          full_name: to.full_name,
          coordinates: to.coordinates,
          type: to.type,
          country_code: to.country_code,
          city_id: to.city_id,
          mainDisplayName: to.name,
          subDisplayName: to.full_name,
          iconDisplayName: to.type,
          iconName: 'HotelIcon',
          isClickedDestinationItem: true,
        },
        date: {
          checkin: checkin,
          checkout: checkout,
        },
        rooms: roomConfigs.map((room) => ({
          adult: room.adults,
          child: room.children.length,
          infant: 0,
        })),
        roomConfigs,
      };

      const filtered = existingSearches.filter(
        (search) => search.destination.id !== to.id,
      );

      const updated = [newSearch, ...filtered].slice(0, MAX_CONTINUE_SEARCHES);

      // Update query cache to trigger re-render in all components
      // TanStack Query with QUERY_PERSIST_CACHE will automatically persist to storage
      queryClient.setQueryData(CONTINUE_SEARCHES_QUERY_KEY, updated);
    },
    [queryClient],
  );

  return {
    continueSearches,
    isLoading: false,
    addContinueSearch,
  };
}
